// Ticket-Titel mit Objektbezug: „0018 Schulstraße 18 · Wasserschaden im Bad".
//
// Grund (User 2026-08-12): In Listen, Mails und Exporten sieht man einem Ticket sonst nicht
// an, zu welchem Haus es gehört — gerade bei aus E-Mails erzeugten Tickets, wo der Betreff
// oft nur „Rückfrage" oder „Schaden" heißt. Objektnummer und Straße stehen deshalb vorn.

export interface TitelObjekt {
  objektnummer?: string | null;
  strasse?: string | null;
  hausnummer?: string | null;
}

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** „0018 Schulstraße 18" — leer, wenn weder Nummer noch Straße bekannt sind. */
export function objektPraefix(o: TitelObjekt | null | undefined): string {
  if (!o) return '';
  const adresse = [text(o.strasse), text(o.hausnummer)].filter(Boolean).join(' ');
  return [text(o.objektnummer), adresse].filter(Boolean).join(' ');
}

/**
 * Vergleichsform: Groß-/Kleinschreibung, Satz- und Leerzeichen egal, ß = ss — sonst gelten
 * „Schulstraße 18" und „schulstrasse18" als verschieden (gleiche Regel wie canonStr in der
 * E-Mail-Objekterkennung).
 */
const kern = (s: string): string => s.toLowerCase().replace(/ß/g, 'ss').replace(/[^a-z0-9]/g, '');

/**
 * Stellt den Objektbezug voran — außer der Titel trägt ihn schon (etwa weil jemand ihn von
 * Hand getippt oder ein Ticket kopiert hat). Ohne Objekt bleibt der Titel unverändert.
 */
/**
 * Titel, die schon mit einer Objektnummer beginnen („#003 …", „055 …", „0014.1 …"), bleiben
 * unangetastet — auch wenn es eine ANDERE Nummer ist als die des verknüpften Objekts. Sonst
 * stünden zwei Nummern nebeneinander und niemand wüsste, welche gilt.
 */
/** „…strasse“ → „…str“, damit „Alemannenstr.“ und „Alemannenstraße“ gleich sind. */
const kurzStrasse = (k: string): string => k.replace(/strasse/g, 'str');

const BEGINNT_MIT_NUMMER = /^[#\s]*\d{3,4}(\.\d+)?(\s|$)/;

export function titelMitObjekt(titel: string, o: TitelObjekt | null | undefined): string {
  const praefix = objektPraefix(o);
  const rein = (titel || '').trim();
  if (!praefix) return rein;
  if (!rein) return praefix;

  const titelKern = kern(rein);
  const nummer = text(o?.objektnummer);
  const adresseKern = kern([text(o?.strasse), text(o?.hausnummer)].filter(Boolean).join(' '));
  // Adresse steht schon im Titel → nichts tun.
  if (adresseKern && titelKern.includes(adresseKern)) return rein;
  // #580: Straßenname allein (auch „Str.“ statt „Straße“) reicht, das Objekt ist erkennbar.
  const strKern = kurzStrasse(kern(text(o?.strasse)));
  if (strKern.length >= 5 && kurzStrasse(titelKern).includes(strKern)) return rein;
  // #580: Titel beginnt mit GENAU dieser Objektnummer, aber ohne Straße („0048 Mängel…“) →
  // Straße ergänzen, damit man das Objekt auch ohne Nummernkenntnis erkennt.
  if (nummer) {
    const m = rein.match(new RegExp(`^[#\\s]*${nummer.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=[\\s·:–-]|$)[\\s·:–-]*`));
    if (m) {
      const rest = rein.slice(m[0].length).trim();
      return rest ? `${praefix} · ${rest}` : praefix;
    }
  }
  if (BEGINNT_MIT_NUMMER.test(rein)) return rein;
  if (nummer && titelKern.startsWith(kern(nummer))) return rein;
  return `${praefix} · ${rein}`;
}
